import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { parsePolicy } from './policy-text.js';

/** The CMS block holding the policy — see sql/migrations/015. */
export const PRIVACY_BLOCK = 'privacy_policy';

/**
 * The privacy policy text, from the CMS.
 *
 * One component for the microsite's /privacy page and the app's register
 * popup, so the text a person consents to is the same text in both places —
 * and the organisers change it in one place, without a release.
 *
 * Takes the frontend's own `api` client, like PartnerFooter. Refetches on a
 * language change: the server localises from the request's language.
 */
export default function PrivacyPolicy({ api }) {
  const { t, i18n } = useTranslation();
  const [state, setState] = useState({ loading: true, block: null, failed: false });

  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, failed: false }));
    api.event
      .content()
      .then((res) => {
        if (!cancelled) setState({ loading: false, block: res?.byKey?.[PRIVACY_BLOCK] || null, failed: false });
      })
      .catch((err) => {
        console.error('[privacy] load failed', err);
        if (!cancelled) setState({ loading: false, block: null, failed: true });
      });
    return () => {
      cancelled = true;
    };
  }, [api, i18n.language]);

  if (state.loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-7 h-7 border-2 border-brand border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const sections = parsePolicy(state.block?.body);
  if (state.failed || !sections.length) {
    return (
      <div className="py-10 text-center">
        <p className="font-heading font-bold uppercase text-white/60 text-lg">{t('common.loadFailed')}</p>
        <p className="text-white/40 text-sm mt-1 font-body">{t('common.checkConnection')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-7">
      {sections.map((s, i) => (
        <section key={i}>
          {s.heading && <h2 className="font-heading font-bold uppercase text-brand text-base mb-2">{s.heading}</h2>}
          {s.text && <p className="text-white/60 text-sm leading-relaxed font-body whitespace-pre-line">{s.text}</p>}
        </section>
      ))}
    </div>
  );
}
